/**
 * Owner Settings — "ศูนย์ควบคุมระบบ" (System Control Panel).
 *
 * แบ่งเป็นหมวดแบบแท็บแทน scroll ยาวหน้าเดียว: บริษัท · รอบเงินเดือน · สูตรเวลา ·
 * เช็คอิน · AI · ความปลอดภัย. ค่าทั้งหมดเก็บใน settings key/value table
 * (ยกเว้นข้อมูลบริษัทที่มี endpoint แยก และ AI ที่เป็นหน้าแยก /owner/ai).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Save, Download, Trash2, Building2, CalendarClock, Clock, MapPin, Sparkles, Shield, ChevronRight, UserPlus, Upload,
  ScrollText, Cloud, LayoutDashboard,
} from 'lucide-react';
import { OwnerAuditContent } from './Audit';
import { OwnerDriveConfigContent } from './DriveConfig';
import { AISettingsContent } from './AISettings';
import { ResetDataSection } from '../../components/ResetDataSection';
import { SettingsOverview } from '../../components/SettingsOverview';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useSettings, useUpdateSettings } from '../../hooks';
import { useAuthStore } from '../../lib/store';
import { apiFetch } from '../../lib/api-client';
import { CompanyProfileForm } from '../../components/CompanyProfileForm';
import { useToast } from '../../components/ui/toast';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

async function downloadCsv(path: string, filename: string, onError?: (msg: string) => void) {
  const jwt = useAuthStore.getState().jwt;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    onError?.(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
  }
}

type State = Record<string, string>;
// 'time' tab ย้ายไปอยู่ใน Time hub แล้ว (`/hr/time#settings`) — TabId ยังต้อง
// คงไว้เพื่อให้ FIELD_GROUPS filter ทำงาน (Time hub import FIELD_GROUPS + filter
// ด้วย tab='time'), แต่ TABS array ไม่โชว์ tab นี้แล้ว
type TabId = 'overview' | 'company' | 'payroll' | 'time' | 'checkin' | 'ai' | 'security' | 'audit' | 'drive' | 'reset';

// Order: setup flow → ใช้บางครั้ง → audit/backup (Owner-design 2026-05-28)
const TABS: Array<{ id: TabId; label: string; icon: typeof Building2 }> = [
  { id: 'overview', label: 'ภาพรวม', icon: LayoutDashboard },
  { id: 'company', label: 'บริษัท', icon: Building2 },
  { id: 'payroll', label: 'รอบเงินเดือน', icon: CalendarClock },
  { id: 'checkin', label: 'เช็คอิน', icon: MapPin },
  { id: 'security', label: 'ความปลอดภัย', icon: Shield },
  { id: 'ai', label: 'AI', icon: Sparkles },
  { id: 'audit', label: 'ประวัติการใช้งาน', icon: ScrollText },
  { id: 'drive', label: 'สำรองข้อมูล (Drive)', icon: Cloud },
  { id: 'reset', label: 'ล้างข้อมูล', icon: Trash2 },
];

interface FieldDef {
  key: string;
  label: string;
  type?: 'time' | 'number' | 'text' | 'select' | 'checkbox' | 'month';
  hint?: string;
  options?: Array<{ value: string; label: string }>;
  step?: string;
  min?: string;
  max?: string;
}

/** กลุ่ม field ที่เก็บใน settings table — แต่ละกลุ่มผูกกับแท็บผ่าน `tab` */
/** hrScope:'paycycle' = กลุ่มที่ฝ่ายบุคคลแก้ได้เองในหน้า ค่าจ้าง → รอบจ่ายเงินเดือน (ยิง /hr/pay-cycle-settings)
 *  ⚠️ คีย์ที่เซฟได้ถูก allowlist ไว้ที่ HR_PAY_CYCLE_SETTING_KEYS ฝั่ง worker — เพิ่มช่องในกลุ่มนี้ต้องเพิ่มคีย์ที่นั่นด้วย */
/** warn(form) — ข้อความเตือนใต้หัวกลุ่มเมื่อค่าที่ติ๊กไว้ขัดกันเอง (ทั้งหน้าเจ้าของและหน้า HR แสดงเหมือนกัน) */
const FIELD_GROUPS: Array<{ title: string; tab: TabId; hrScope?: 'paycycle'; fields: FieldDef[]; warn?: (form: Record<string, string>) => string | null }> = [
  {
    title: 'รอบจ่ายเงินเดือน',
    tab: 'payroll',
    hrScope: 'paycycle',
    fields: [
      { key: 'CUTOFF_DAY', label: 'วันปิดงวดเงินเดือน', type: 'number', min: '1', max: '31', hint: 'ใช้จริงกับการลา cron แจ้งเตือน AI rule และการปิดงวด (1-31)' },
      // fb#158 — 31 = ทุกวันสิ้นเดือน (ลูกค้าไม่อยากให้สลิปเขียน "ทุกวันที่ 31")
      { key: 'PAYDAY_DAY', label: 'วันจ่ายเงินเดือน', type: 'select', hint: 'พิมพ์บนท้ายสลิป · ทุกวันสิ้นเดือน = เดือนที่มี 28/29/30 วันจ่ายวันสุดท้ายของเดือน', options: [
        { value: '', label: '— ไม่ระบุ —' },
        { value: '31', label: 'ทุกวันสิ้นเดือน' },
        ...Array.from({ length: 30 }, (_, i) => ({ value: String(i + 1), label: `ทุกวันที่ ${i + 1}` })),
      ] },
      {
        key: 'BACKDATED_MODE',
        label: 'นโยบายลาย้อนหลัง',
        type: 'select',
        options: [
          { value: 'lenient', label: 'ยืดหยุ่น — รับลาย้อนหลังแล้วส่งให้อนุมัติ' },
          { value: 'strict', label: 'เข้มงวด — ปฏิเสธลาย้อนหลังเกิน cutoff' },
        ],
      },
    ],
  },
  {
    // ใครอนุมัติเงินเดือนได้ = เจ้าของเท่านั้น (ให้ HR แก้เอง = ยกสิทธิ์ให้ตัวเอง)
    title: 'การอนุมัติเงินเดือน',
    tab: 'payroll',
    fields: [
      { key: 'SOLO_MODE', label: 'โหมดทำคนเดียว', type: 'checkbox', hint: 'เปิด = คนที่ทำเงินเดือน (รวมฝ่ายบุคคล) กดอนุมัติเองได้ ไม่ต้องรอเจ้าของ — ใช้กับบริษัทที่มีคนดูเงินเดือนคนเดียว · บริษัทที่ต้องให้ผู้บริหารอนุมัติ ให้ปิดช่องนี้' },
      { key: 'OWNER_ONLY_PAYROLL_APPROVAL', label: 'อนุมัติเงินเดือนได้เฉพาะผู้บริหาร', type: 'checkbox', hint: 'ติ๊ก = ฝ่ายบุคคลอนุมัติแทนไม่ได้เลย แม้เลยกำหนดแล้ว (ต้องปิดโหมดทำคนเดียวด้วย) · ไฟล์โอนธนาคารออกได้หลังผู้บริหารอนุมัติและปิดงวดแล้วเท่านั้น' },
      { key: 'SALARY_ONLY', label: 'โหมดเงินเดือนล้วน', type: 'checkbox', hint: 'เปิด = ไม่ใช้ระบบลงเวลา คิดเงินจากฐานเงินเดือน เงินเพิ่ม รายการหัก และภาษี/ประกันสังคม (จ่ายเต็ม ไม่หักสาย/ขาด ไม่จ่าย OT)' },
      { key: 'BACKUP_APPROVER_LAG_DAYS', label: 'HR backup approve หลังสิ้นงวด (วัน)', type: 'number', min: '0', max: '31', hint: 'ใช้เมื่อ Owner ไม่อนุมัติทันเวลา (0–31 วัน)' },
      {
        key: 'COMMISSION_LAG_MONTHS',
        label: 'จ่ายคอมมิชชั่นล่าช้า',
        type: 'select',
        options: [
          { value: '0', label: 'เดือนเดียวกัน' },
          { value: '1', label: 'เลื่อน 1 เดือน' },
        ],
      },
      { key: 'VARIANCE_THRESHOLD', label: 'เกณฑ์เตือนเงินเดือนเปลี่ยน (%)', type: 'number', hint: 'ใช้กับตรวจก่อนปิดงวดและ smart warning' },
    ],
  },
  {
    title: 'ทดลองงาน',
    tab: 'company',
    fields: [
      { key: 'PROBATION_MONTHS', label: 'ระยะทดลองงาน (เดือน)', type: 'number', min: '0', max: '12', hint: 'พนักงานใหม่จะครบทดลองงาน = วันเริ่มงาน + จำนวนเดือนนี้ · ใส่ 0 = บริษัทไม่ใช้ระบบทดลองงาน (ปิดคิวและการแจ้งเตือน)' },
      { key: 'PROBATION_NOTICE_DAYS', label: 'เตือนล่วงหน้าก่อนครบ (วัน)', type: 'number', min: '0', max: '60', hint: 'แจ้งเตือนเจ้าของ/HR ล่วงหน้ากี่วัน แล้วเตือนอีกครั้งวันครบกำหนด · ถ้ายังไม่ตัดสินจะตามซ้ำทุกวันจันทร์' },
    ],
  },
  {
    title: 'ไฟล์โอนเงินเดือน (ธนาคาร)',
    tab: 'payroll',
    fields: [
      {
        key: 'PRIMARY_BANK',
        label: 'ธนาคารหลักของบริษัท',
        type: 'select',
        options: [
          { value: 'kbank', label: 'กสิกรไทย — ไฟล์ K-Cash Connect (.txt)' },
          { value: 'scb', label: 'ไทยพาณิชย์ — ไฟล์ SCB Business Anywhere (.txt)' },
          { value: 'other', label: 'อื่นๆ — ไฟล์ CSV ทั่วไป' },
        ],
        hint: 'รูปแบบไฟล์โอนเงินเดือนที่ดาวน์โหลดไปอัปโหลดเข้าระบบธนาคาร · ก่อนใช้ไฟล์ .txt ครั้งแรก ควรเทียบกับไฟล์ตัวอย่างจากธนาคารของบริษัท',
      },
      { key: 'COMPANY_BANK_ACCOUNT_NO', label: 'เลขบัญชีบริษัท (ต้นทาง)', type: 'text', hint: 'บัญชีที่ใช้ตัดเงินเดือน — ใส่เฉพาะตัวเลข ใช้ในไฟล์โอนของธนาคาร' },
    ],
  },
  {
    // fb#464 TN 22 ต.ค. 2569 — ปุ่มออกไฟล์ สปส.1-10 บอกให้ "ตั้งที่ ตั้งค่าระบบ" แต่ไม่เคยมีช่องให้กรอก
    title: 'ไฟล์นำส่งประกันสังคม (สปส.1-10)',
    tab: 'payroll',
    hrScope: 'paycycle',
    fields: [
      { key: 'SSO_EMPLOYER_ACCOUNT_NO', label: 'เลขที่บัญชีนายจ้าง (ประกันสังคม)', type: 'text', hint: 'เลข 10 หลักตามหนังสือขึ้นทะเบียนนายจ้างของสำนักงานประกันสังคม (ไม่ใช่เลขผู้เสียภาษี 13 หลัก) · พิมพ์ขีดได้ ระบบเก็บเฉพาะตัวเลข · ใช้ออกไฟล์และ PDF สปส.1-10 ในหน้าปิดงวด' },
      { key: 'SSO_BRANCH_SEQ', label: 'ลำดับที่สาขา (ประกันสังคม)', type: 'text', hint: 'สำนักงานใหญ่ = 0 หรือเว้นว่าง · สาขาใส่เลขลำดับที่สาขาตามที่ขึ้นทะเบียนกับประกันสังคม (ไม่เกิน 6 หลัก)' },
    ],
  },
  {
    title: 'ไฟล์สำหรับชีตเงินเดือน',
    tab: 'payroll',
    fields: [
      { key: 'PAYSHEET_DAY_DIVISOR', label: 'หารเงินเดือนเป็นค่าแรงต่อวัน', type: 'number', min: '1', max: '31', hint: 'ใช้คิดค่าแรงวันนักขัตฤกษ์ ค่าทำงานวันหยุดประจำ (ช่อง "พิเศษ วันอาทิตย์") และหักขาดงาน/ลาไม่รับเงิน · 30 = เงินเดือน 12,000 ได้วันละ 400 · มาสายไม่หักเงิน แสดงวันที่สายในช่องรายละเอียดให้ดูเท่านั้น' },
      // fb#523 (TN 26 ต.ค. 2569) — ชีตเดิมคิด OT ชม.ละ 60 ทุกคน · ลูกค้าขอคิดตามกฎหมาย
      {
        key: 'PAYSHEET_OT_MODE',
        label: 'วิธีคิดค่า OT',
        type: 'select',
        hint: 'ตามกฎหมาย = ค่าจ้างต่อชั่วโมงของแต่ละคน (เงินเดือน ÷ ตัวหารด้านบน ÷ ชั่วโมงทำงานต่อวัน · รายวัน = ค่าแรงต่อวัน ÷ ชั่วโมงทำงานต่อวัน) × 1.5 สำหรับ OT วันทำงาน · × 3 สำหรับ OT วันหยุดที่เกินชั่วโมงปกติ · เช่น เงินเดือน 12,000 ทำงานวันละ 8 ชม. = ชม.ละ 50 → OT ชม.ละ 75 · ช่องรายละเอียดในไฟล์บอกอัตราของแต่ละคน',
        options: [
          { value: 'legal', label: 'ตามกฎหมาย — ค่าจ้างต่อชั่วโมงของแต่ละคน × 1.5 / × 3' },
          { value: 'flat', label: 'อัตราเดียวทุกคน — ตามช่องค่า OT ด้านล่าง' },
        ],
      },
      { key: 'PAYSHEET_OT_RATE', label: 'ค่า OT อัตราเดียวทุกคน (บาท/ชั่วโมง)', type: 'number', min: '0', hint: 'ใช้เมื่อเลือก "อัตราเดียวทุกคน" ด้านบนเท่านั้น · คูณชั่วโมง OT ที่อนุมัติ' },
      // fb#415 (TN 19 ต.ค. 2569) — ค่าทำงานวันหยุดประจำให้เฉพาะบางคน ที่เหลือบวกเอง
      { key: 'PAYSHEET_DAYOFF_COLUMN_OFF', label: 'ไม่เติมช่อง "พิเศษ วันอาทิตย์" ให้ทุกคน', type: 'checkbox', hint: 'เปิด = ช่องหน่วยจ่าย/จำนวนวันของ "พิเศษ วันอาทิตย์" เว้นว่างทุกคน แม้มาทำงานวันหยุดประจำ · วันที่ที่มาทำยังขึ้นในช่องรายละเอียดให้ดู แล้วพิมพ์ให้คนที่ได้จริงเองในชีตเงินเดือนช่องนั้น (หรือใส่รหัสในช่องถัดไปให้ระบบเติมให้) · ปิด = เติมให้ทุกคนที่มาทำวันหยุดประจำตามค่าแรงต่อวันของแต่ละคน' },
      { key: 'PAYSHEET_DAYOFF_EMP_CODES', label: 'ยกเว้น: รหัสพนักงานที่ยังเติมช่องพิเศษวันอาทิตย์ให้', type: 'text', hint: 'ใช้คู่กับตัวเลือกด้านบน · พิมพ์รหัสพนักงาน คั่นด้วยจุลภาค เช่น EMP0015 · คนในรายการได้ตามค่าแรงต่อวันของตัวเอง (เงินเดือน ÷ ตัวหาร) ทุกวันหยุดประจำที่มาทำ · รหัสที่ไม่มีในระบบจะบันทึกไม่ผ่าน' },
    ],
  },
  {
    title: 'รายชั่วโมง / ประกันสังคม / ภาษี ณ ที่จ่าย',
    tab: 'payroll',
    hrScope: 'paycycle',
    // fb#439 TN Riches — HR ติ๊ก "ชั่วโมงเต็ม" + "ในกะ" ไว้ตั้งแต่ 28 ก.ย. แต่สวิตช์หลักปิด → รายชั่วโมงได้ 0 บาทโดยไม่มีใครเตือน
    warn: (f) => f.HOURLY_HOURS_FROM_SCAN !== 'true' && (f.HOURLY_SCAN_WHOLE_HOURS === 'true' || f.HOURLY_SCAN_WITHIN_SHIFT === 'true' || f.HOURLY_SCAN_BREAK_DEDUCT === 'true')
      ? 'เปิด "นับเฉพาะชั่วโมงเต็ม" / "นับเฉพาะชั่วโมงในกะ" / "หักพัก" ไว้ แต่สวิตช์หลัก "รายชั่วโมง: ดึงชั่วโมงจากการสแกน" ยังปิด — สองข้อนั้นจะไม่มีผล และพนักงานรายชั่วโมงที่ไม่ได้กรอกชั่วโมงเองจะได้ค่าจ้าง 0 ในร่างเงินเดือน'
      : null,
    fields: [
      { key: 'HOURLY_HOURS_FROM_SCAN', label: 'รายชั่วโมง: ดึงชั่วโมงจากการสแกน', type: 'checkbox', hint: 'เปิด = พนักงานรายชั่วโมงที่ไม่ได้กรอกชั่วโมงเองในงวดนั้น ระบบรวมชั่วโมงจากสแกนเข้า–ออกแต่ละวันให้ (ไม่หักพักเที่ยง · กะข้ามเที่ยงคืนนับถูก · วันที่สแกนไม่ครบคู่หรือเกิน 16 ชม. ไม่นับและขึ้นเตือนตอนคำนวณ) · กรอกชั่วโมงเองเมื่อไรใช้ค่าที่กรอกแทน' },
      { key: 'HOURLY_SCAN_WHOLE_HOURS', label: 'รายชั่วโมง: นับเฉพาะชั่วโมงเต็ม', type: 'checkbox', hint: 'เปิด = ชั่วโมงจากสแกน เวลาเข้าปัดขึ้นเป็นชั่วโมงถัดไป เวลาออกปัดลง ไม่มีเศษนาที · เช่น เข้า 05.54 นับ 6.00 · ออก 15.07 นับ 15.00 = 9 ชม. · ใช้เมื่อเปิดดึงชั่วโมงจากการสแกน · เปลี่ยนแล้วกดรันร่างใหม่เพื่อให้ยอดเปลี่ยนตาม' },
      { key: 'HOURLY_SCAN_WITHIN_SHIFT', label: 'รายชั่วโมง: นับเฉพาะชั่วโมงในกะ', type: 'checkbox', hint: 'เปิด = ชั่วโมงจากสแกนนับเฉพาะช่วงเวลากะในตารางเวรของวันนั้น มาก่อนเวลาเข้ากะหรืออยู่เลยเวลาเลิกกะไม่นับ · เช่น กะ 18.00–00.00 สแกน 17.20 ออก 22.05 = นับ 18.00–22.05 · วันที่ไม่มีกะ (มาทำวันหยุด) นับตามสแกน · วันที่สแกนไม่ตรงกะเลย (สลับกะแต่ไม่ได้แก้ตาราง) ไม่นับและขึ้นเตือนตอนรันร่าง ให้แก้ตารางเวรหรือกรอกชั่วโมงเอง · กะยืดหยุ่นนับตามสแกน · เปลี่ยนแล้วกดรันร่างใหม่' },
      // fb#500 TN 25 ต.ค. 2569 — "รายชั่วโมงที่เกิน 6 ชม. ยังไม่หักเวลาพักออก"
      { key: 'HOURLY_SCAN_BREAK_DEDUCT', label: 'รายชั่วโมง: เกิน 6 ชม. หักพัก 1 ชม.', type: 'checkbox', hint: 'เปิด = วันไหนชั่วโมงจากสแกนเกิน 6 ชม. หักเวลาพัก 1 ชม. ออก (หักแล้วไม่ต่ำกว่า 6 ชม.) · เช่น 07.00–15.00 = 8 ชม. นับ 7 · 06.00–12.00 = 6 ชม. ไม่หัก · คิดหลังปัดชั่วโมงเต็ม/ตัดนอกกะ · ไฟล์ชั่วโมงรายวันบอกวันที่หักพัก · ใช้เมื่อเปิดดึงชั่วโมงจากการสแกน · เปลี่ยนแล้วกดรันร่างใหม่' },
      { key: 'SSO_BASE_SALARY_ONLY', label: 'ประกันสังคม: คิดจากเงินเดือนฐานอย่างเดียว', type: 'checkbox', hint: 'เปิด = หักประกันสังคม 5% จากเงินเดือน (หรือค่าแรงรายวัน/รายชั่วโมง) อย่างเดียว ค่าล่วงเวลา ค่านายหน้า ค่าคอม เงินพิเศษ และรายการเงินเพิ่มทุกชนิดไม่นำมาคิด · ใช้ทั้งตอนรันร่างและช่อง สปส. ในไฟล์ชีตเงินเดือน · ปิด = รวมค่าล่วงเวลาและรายการที่ติ๊กเข้าประกันสังคม · เปลี่ยนแล้วกดรันร่างใหม่' },
      { key: 'WHT_PND3_RATE_PCT', label: 'อัตราหักภาษี ณ ที่จ่าย ภ.ง.ด.3 (%)', type: 'number', step: '0.5', min: '0.5', max: '15', hint: 'ใช้กับพนักงานที่ตั้ง "ไม่หักประกันสังคม — หักภาษี ณ ที่จ่าย (ภ.ง.ด.3)" ในประวัติพนักงาน · คิดจากรายรับรวมทั้งก้อน · ค่าเริ่มต้น 3' },
    ],
  },
  {
    // fb#254 — คีย์อยู่ใน HR_PAY_CYCLE_SETTING_KEYS ฝั่ง worker แล้ว (WELFARE_FUND_KEYS) · ตรวจค่าที่ welfareFundSettingError
    title: 'กองทุนสงเคราะห์ลูกจ้าง',
    tab: 'payroll',
    hrScope: 'paycycle',
    fields: [
      { key: 'WELFARE_FUND_ENABLED', label: 'หักเงินสะสมกองทุนสงเคราะห์ลูกจ้าง', type: 'checkbox', hint: 'ตาม พ.ร.บ.คุ้มครองแรงงาน ลูกจ้างจ่ายเงินสะสม 0.25% ของค่าจ้าง (นายจ้างจ่ายสมทบอีก 0.25% เอง ไม่หักจากลูกจ้าง) เริ่ม 1 ต.ค. 2569 · รัฐกำลังพิจารณาเลื่อนวันเริ่มออกไปประมาณ 6 เดือน ยังไม่ประกาศ ควรตรวจประกาศกรมสวัสดิการและคุ้มครองแรงงานก่อนเปิด · เปิด = หักจากเงินสุทธิ แสดงบนสลิปและไฟล์ชีตเงินเดือน · ฐาน = ค่าจ้าง (เงินเดือน/ค่าแรงหลังหักขาดและลาไม่รับเงิน + ค่าคอม + เงินเพิ่มที่ติ๊กเข้าประกันสังคม) ไม่รวมค่าล่วงเวลา · ไม่ลดเงินได้ที่ใช้คิดภาษี · ไม่หักคนที่ตั้ง "ไม่หักอะไรเลย" และคนที่หักภาษี ภ.ง.ด.3 · เปลี่ยนแล้วกดรันร่างใหม่' },
      { key: 'WELFARE_FUND_RATE_PCT', label: 'อัตราเงินสะสมส่วนลูกจ้าง (%)', type: 'number', step: '0.01', min: '0.01', max: '5', hint: 'ค่าเริ่มต้น 0.25 · กฎหมายกำหนดให้ขึ้นเป็น 0.5 ภายหลัง แก้ช่องนี้เมื่อมีประกาศ' },
      { key: 'WELFARE_FUND_WAGE_CAP', label: 'เพดานค่าจ้างต่อเดือน (บาท)', type: 'number', min: '1', hint: 'เว้นว่าง = ไม่มีเพดาน (ยังไม่พบประกาศเพดาน) · ใส่ค่า = คิดจากค่าจ้างไม่เกินยอดนี้' },
      { key: 'WELFARE_FUND_START_PERIOD', label: 'เริ่มหักตั้งแต่งวด', type: 'month', hint: 'ปี ค.ศ.-เดือน เช่น 2026-10 = งวดตุลาคม 2569 · งวดก่อนหน้านี้ไม่หัก · ถ้ารัฐเลื่อนวันเริ่ม แก้ช่องนี้แทนการปิด' },
    ],
  },
  {
    // fb#438 — คีย์อยู่ใน HR_PAY_CYCLE_SETTING_KEYS ฝั่ง worker (SHORTFALL_KEYS) · ตรวจค่าที่ shortfallSettingError
    title: 'หักค่าจ้างเมื่อเข้างานไม่ครบเวลา',
    tab: 'payroll',
    hrScope: 'paycycle',
    fields: [
      { key: 'SHORTFALL_DEDUCT_ENABLED', label: 'หักค่าจ้างตามนาทีที่เข้างานไม่ครบ', type: 'checkbox', hint: 'เปิด = ตอนสรุปเวลา ระบบนับนาทีที่หายของแต่ละวันทำงาน = มาสาย (หลัง "สายได้ไม่เกิน") + ออกก่อนเวลาเลิกกะ เทียบกะในตารางเวรของวันนั้น (ไม่เกินชั่วโมงทำงานของกะ) · ไม่สแกนออก = ถือว่าเลิกตามกะ · หัก = นาทีที่หาย × ค่าแรงต่อชั่วโมง (รายเดือน: เงินเดือน ÷ 30 ÷ ชั่วโมงทำงานต่อวัน · รายวัน: ค่าแรงรายวัน ÷ ชั่วโมงทำงานต่อวัน) · รายชั่วโมงไม่หัก · กะยืดหยุ่น วันลา วันหยุด และคนที่ไม่ต้องสแกนไม่คิด · ทุกวันที่หายขึ้นการ์ด "เข้างานไม่ครบเวลา" ในหน้ากิจกรรมผิดปกติ วันที่ผู้บริหารอนุญาต (เช่น สั่งเข้าบ่ายหลังทำ OT คืนก่อน) ให้กด "ยกเว้น ไม่หัก" พร้อมเหตุผล · ยอดหักขึ้นบนสลิปเป็นบรรทัด "หักเข้างานไม่ครบเวลา" และรวมในช่อง ขาด/ลา/มาสาย ของไฟล์ชีตเงินเดือน · เปลี่ยนแล้วต้องกด "เริ่มสรุปเวลา" ใหม่แล้วรันร่างใหม่' },
      { key: 'SHORTFALL_DEDUCT_FROM_PERIOD', label: 'หักเข้างานไม่ครบเวลา ตั้งแต่งวด', type: 'month', hint: 'ปี ค.ศ.-เดือน เช่น 2026-10 = งวดตุลาคม 2569 · งวดก่อนหน้านี้ไม่หัก · เว้นว่าง = ทุกงวดที่ยังไม่ปิด' },
    ],
  },
  {
    title: 'วันทำงานและสูตรเวลา',
    // ⚠️ กลุ่มนี้ถูก reuse ในหน้า เวลาทำงาน → ตั้งค่าเวลา ซึ่งเซฟผ่าน /hr/time-settings
    //    เพิ่มช่องตรงนี้ต้องไปเพิ่มคีย์ใน HR_TIME_SETTING_KEYS (handlers/owner.ts) ด้วย
    tab: 'time',
    fields: [
      { key: 'WORKDAYS_PER_WEEK', label: 'จำนวนวันทำงานต่อสัปดาห์', type: 'number', min: '1', max: '7', hint: '1-7' },
      { key: 'WEEKEND_DAYS', label: 'วันหยุดประจำสัปดาห์', type: 'text', hint: 'เลขวันแบบ ISO คั่นด้วย comma: 1=จันทร์ … 7=อาทิตย์ เช่น 6,7' },
      { key: 'HOURS_PER_WORKDAY', label: 'ชั่วโมงทำงานต่อวัน', type: 'number', step: '0.25', min: '1', max: '24', hint: 'ใช้คิดรายวัน/รายชั่วโมง/OT' },
      { key: 'LATE_GRACE_MINUTES', label: 'สายได้ไม่เกิน (นาที)', type: 'number', min: '0', max: '240', hint: 'ใช้กับ shift-aware attendance' },
      { key: 'OT_WEEKLY_CAP_HOURS', label: 'เพดาน OT ต่อสัปดาห์ (ชั่วโมง)', type: 'number', step: '0.25', min: '0', max: '168', hint: 'ค่าแรงไทยทั่วไปใช้ 36 ชม./สัปดาห์' },
      { key: 'REQUIRE_CLOCK_OUT', label: 'ให้สแกนเลิกงานด้วย', type: 'checkbox', hint: 'ปิด = สแกนเฉพาะขาเข้า ทั้งบริษัท — ไม่มีสแกนออกไม่ถือว่า "สแกนไม่ครบ" ไม่ขึ้นรายการผิดปกติ ไม่ตามให้สแกนออก ถือว่าเลิกงานตามกะ · มาสายยังนับจากสแกนเข้าตามปกติ · ตั้งเฉพาะบางกะได้ที่ กะ/ตารางเวร → กะยืดหยุ่น "สแกนเข้าอย่างเดียว"' },
      { key: 'SELF_CONFIRM_CLOCKOUT', label: 'ให้พนักงานยืนยันเวลาเลิกงานเองได้', type: 'checkbox', hint: 'เปิด = ลืมสแกนออก พนักงานกด "เลิกตามกะ" หรือระบุเวลาเองได้ทันที ไม่ผ่าน HR · ปิด = ต้องยื่น "รับรองเวลาเข้า-ออกงาน" พร้อมเหตุผล ให้ HR อนุมัติก่อน (กันออกก่อนเวลาแล้วกดรับเวลาเต็มกะ)' },
    ],
  },
  {
    title: 'จำนวนรอบและเวลาเช็คอิน',
    tab: 'checkin',
    fields: [
      { key: 'REQUIRE_LUNCH_SCAN', label: 'ให้สแกนพักเที่ยงด้วย (4 รอบ)', type: 'checkbox', hint: 'ปิด = เช็คอิน 2 รอบ (เข้างาน/เลิกงาน) · เปิด = 4 รอบ (เพิ่มออกพัก/กลับจากพัก)' },
      { key: 'WORK_MORNING_IN_TIME', label: 'เข้างาน (เช้า)', type: 'time' },
      { key: 'WORK_LUNCH_OUT_TIME', label: 'พักเที่ยง (ออกพัก)', type: 'time', hint: 'ใช้เมื่อเปิดสแกนพักเที่ยง' },
      { key: 'WORK_LUNCH_IN_TIME', label: 'กลับจากพัก', type: 'time', hint: 'ใช้เมื่อเปิดสแกนพักเที่ยง' },
      { key: 'WORK_EVENING_OUT_TIME', label: 'เลิกงาน', type: 'time' },
    ],
  },
  {
    title: 'การเตือน (cron)',
    tab: 'checkin',
    fields: [
      { key: 'CHECKIN_GRACE_MINUTES', label: 'รอ (นาที) ก่อนเตือนครั้งแรก', type: 'number', hint: 'เช่น 15 = เลยเวลา 15 นาทียังไม่เช็คอินจึงเตือน' },
      { key: 'REMINDER_INTERVAL_MINUTES', label: 'เว้นช่วงระหว่างเตือน (นาที)', type: 'number', hint: 'เริ่มต้น 5' },
      { key: 'REMINDER_COUNT_MAX', label: 'จำนวนครั้งเตือนสูงสุด', type: 'number', hint: 'เริ่มต้น 3' },
    ],
  },
  {
    title: 'พื้นที่ออฟฟิศ (geofence)',
    tab: 'checkin',
    fields: [
      { key: 'GEOFENCE_ENABLED', label: 'ตรวจพิกัดตอนเช็คอิน', type: 'checkbox', hint: 'ปิด = เช็คอินจากที่ไหนก็ผ่าน (เหมาะกับงานนอกสถานที่/หลายสาขา)' },
      { key: 'OFFICE_LATITUDE', label: 'Latitude', type: 'text', hint: 'เช่น 13.7563' },
      { key: 'OFFICE_LONGITUDE', label: 'Longitude', type: 'text', hint: 'เช่น 100.5018' },
      { key: 'OFFICE_RADIUS_METERS', label: 'รัศมีที่อนุญาต (เมตร)', type: 'number', min: '20', max: '5000', hint: 'เริ่มต้น 200 — ต่ำกว่า 20 ม. เสี่ยงสแกนไม่ผ่านเพราะ GPS คลาดเคลื่อน' },
    ],
  },
];


export { FIELD_GROUPS };
export type { FieldDef, TabId };
export function Field({ f, value, onChange }: { f: FieldDef; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label">{f.label}</label>
      {f.type === 'select' ? (
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          {(f.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : f.type === 'checkbox' ? (
        <button
          type="button"
          onClick={() => onChange(value === 'true' ? 'false' : 'true')}
          className={`relative h-7 w-12 rounded-full transition ${value === 'true' ? 'bg-brand-600' : 'bg-gray-300'}`}
          aria-pressed={value === 'true'}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition ${value === 'true' ? 'left-6' : 'left-1'}`} />
        </button>
      ) : (
        <input
          type={f.type ?? 'text'}
          step={f.step}
          min={f.min}
          max={f.max}
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.hint}
        />
      )}
      {f.hint && <div className="text-xs text-gray-500 mt-1">{f.hint}</div>}
    </div>
  );
}

export function OwnerSettings() {
  const { data, isLoading, error, refetch } = useSettings();
  const update = useUpdateSettings();
  const toast = useToast();
  // Pre-fill tab from URL hash so legacy /owner/audit and /owner/drive
  // redirects (Navigate → /owner/settings#audit / #drive) land on the
  // right tab. Only honor IDs that actually exist in TABS — 'time' is
  // gone from the UI (lives in Time hub) so a stray #time stays on
  // 'company' instead of rendering an unreachable section.
  const [tab, setTab] = useState<TabId>(() => {
    if (typeof window === 'undefined') return 'company';
    const h = window.location.hash.replace('#', '');
    return TABS.some((t) => t.id === h) ? (h as TabId) : 'overview';
  });
  // Mirror tab into hash so reload / share-link works.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  // sync tab จาก #hash เมื่อ navigate ผ่าน sidebar (เมนูย่อย)
  const __loc = useLocation();
  useEffect(() => {
    const h = __loc.hash.replace('#', '');
    if (TABS.some((t) => t.id === h)) setTab(h as TabId);
  }, [__loc.hash]);

  const [form, setForm] = useState<State>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading) {
    return (
      <AdminLayout title="ตั้งค่าระบบ">
        <Loading />
      </AdminLayout>
    );
  }
  if (error || !data) {
    return (
      <AdminLayout title="ตั้งค่าระบบ">
        <LoadError error={error} onRetry={() => refetch()} />
      </AdminLayout>
    );
  }

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function handleSave() {
    // เช็คค่าตัวเลขที่มีช่วงจำกัดก่อนส่ง — ค่าผิดแม้ช่องเดียวทำให้ทั้งฟอร์มเซฟไม่ได้
    // (เดิม error ถูกกลืนเงียบ ผู้ใช้เลยเห็นเหมือน "กดเซฟไม่ได้")
    for (const g of FIELD_GROUPS) {
      for (const f of g.fields) {
        if (f.type !== 'number') continue;
        const raw = form[f.key];
        if (raw === undefined || raw === '') continue;
        const n = Number(raw);
        const lo = f.min !== undefined ? Number(f.min) : undefined;
        const hi = f.max !== undefined ? Number(f.max) : undefined;
        if (!Number.isFinite(n) || (lo !== undefined && n < lo) || (hi !== undefined && n > hi)) {
          const range = lo !== undefined && hi !== undefined ? ` (ต้องอยู่ระหว่าง ${lo}–${hi})` : '';
          toast.error(`ค่าในช่อง "${f.label}" ไม่ถูกต้อง${range}`);
          return;
        }
      }
    }
    try {
      await update.mutateAsync(form);
      setSaved(true);
      toast.success('บันทึกการตั้งค่าแล้ว');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const groupsForTab = FIELD_GROUPS.filter((g) => g.tab === tab);

  return (
    <AdminLayout title="ตั้งค่าระบบ">
      {/* แท็บหมวดหมู่ — ศูนย์ควบคุมระบบ */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${
                active ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <SettingsOverview />}

      {/* บริษัท — ฟอร์มเดียวกับที่ HR (edit_company) ใช้ในหน้า OwnerOnly (fb#176) */}
      {tab === 'company' && <CompanyProfileForm />}

      {/* ผูกบริษัทเพิ่ม — Owner ที่ดูแลหลายบริษัท */}
      {tab === 'company' && (
        <div className="card p-4 border-brand-200">
          <div className="font-semibold mb-1">ดูแลหลายบริษัท?</div>
          <p className="text-xs text-gray-500 mb-3">
            บัญชี LINE เดียวผูกได้หลายบริษัท — สลับไปมาได้จาก dropdown มุมซ้ายบน
          </p>
          <div className="grid grid-cols-1 gap-2">
            <Link to="/pair" className="btn-secondary text-sm justify-center">
              <Building2 className="w-4 h-4" /> ผูกกับบริษัทที่มีอยู่แล้ว (ใส่รหัสจาก Owner)
            </Link>
            <Link to="/signup" className="btn-secondary text-sm justify-center">
              <UserPlus className="w-4 h-4" /> สมัครบริษัทใหม่
            </Link>
          </div>
        </div>
      )}

      {/* รอบเงินเดือน / สูตรเวลา / เช็คอิน — ค่าใน settings table */}
      {groupsForTab.length > 0 && (
        <>
          {groupsForTab.map((group) => (
            <div key={group.title} className="card p-4 mb-3">
              <div className="font-semibold mb-3">{group.title}</div>
              {group.warn?.(form) && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">{group.warn(form)}</div>
              )}
              <div className="space-y-3">
                {group.fields.map((f) => (
                  <Field key={f.key} f={f} value={form[f.key] ?? ''} onChange={(v) => setField(f.key, v)} />
                ))}
              </div>
            </div>
          ))}
          <button onClick={handleSave} disabled={update.isPending} className="btn-primary w-full">
            <Save className="w-5 h-5" />
            {update.isPending ? 'กำลังบันทึก…' : saved ? 'บันทึกแล้ว ✓' : 'บันทึก'}
          </button>
        </>
      )}

      {/* AI — absorbed from /owner/ai */}
      {tab === 'ai' && <AISettingsContent />}

      {/* ความปลอดภัย — export + danger zone */}
      {tab === 'security' && (
        <>
          <WebLoginZone />
          <div className="card p-4 mb-3">
            <div className="font-semibold mb-2">Export ข้อมูล (CSV)</div>
            <p className="text-xs text-gray-500 mb-3">สำรองข้อมูลออกมาเก็บเอง — UTF-8 BOM เปิดใน Excel ภาษาไทยได้</p>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => downloadCsv('/owner/export/employees', 'employees.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> พนักงานทั้งหมด
              </button>
              <button onClick={() => downloadCsv(`/owner/export/leaves?year=${new Date().getFullYear()}`, 'leaves-ot.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> ใบลา + OT ปีนี้
              </button>
              <button onClick={() => downloadCsv(`/owner/export/payroll?year=${new Date().getFullYear()}`, 'payroll.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> Payroll ปีนี้
              </button>
            </div>
          </div>
          <EmployeePortableZone />
          <DeleteTenantZone />
        </>
      )}

      {/* ประวัติการใช้งานระบบ — absorbed from /owner/audit */}
      {tab === 'audit' && <OwnerAuditContent />}

      {/* สำรองข้อมูลขึ้น Drive — absorbed from /owner/drive */}
      {tab === 'drive' && <OwnerDriveConfigContent />}
      {tab === 'reset' && <ResetDataSection />}
    </AdminLayout>
  );
}

/**
 * Web login enable/manage section. Owner sets an email + password here
 * (while logged in via LIFF) so they can later log in from a desktop
 * browser at /login without LINE.
 */
function WebLoginZone() {
  const toast = useToast();
  const [linked, setLinked] = useState<{ email: string | null; last_login_at?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch<{ email: string | null; last_login_at?: string | null }>('/me/web-identity');
        if (!cancelled) setLinked(r);
      } catch {
        // 401/etc → leave linked null, user will see "ยังไม่ได้ตั้ง"
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    if (!email.trim() || password.length < 8) {
      toast.error('กรอกอีเมลและรหัสผ่านอย่างน้อย 8 ตัว');
      return;
    }
    if (password !== confirm) {
      toast.error('รหัสผ่านยืนยันไม่ตรงกัน');
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch<{ email: string; linked: boolean }>('/me/web-set-password', {
        method: 'POST',
        body: { email: email.trim(), password },
      });
      setLinked({ email: r.email });
      setEmail('');
      setPassword('');
      setConfirm('');
      toast.success('ตั้งรหัสผ่านเรียบร้อย — ใช้เข้าระบบที่ /login ได้');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตั้งรหัสผ่านไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold mb-2">🔐 เข้าระบบผ่านเว็บ (ไม่ต้องใช้ LINE)</div>
      <p className="text-xs text-gray-500 mb-3">
        ตั้ง email + รหัสผ่าน แล้วเข้าใช้งานจาก desktop ที่ <code className="font-mono">/login</code> ได้ —
        บัญชีเดียวใช้ได้ทั้ง PayPilot / JobPilot / EvalPilot ที่ผูกบริษัทไว้
      </p>

      {loading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : (
        <>
          {linked?.email ? (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2 mb-3">
              ✓ เปิดใช้งานแล้ว: <span className="font-mono">{linked.email}</span>
              {linked.last_login_at && (
                <div className="text-gray-500 mt-0.5">
                  เข้าใช้ล่าสุด: {new Date(linked.last_login_at).toLocaleString('th-TH')}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mb-3">
              ยังไม่ได้ตั้ง — กรอกแบบฟอร์มด้านล่างเพื่อเปิดใช้งาน
            </div>
          )}

          <div className="space-y-2">
            <div>
              <label className="label">อีเมล</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={linked?.email ?? 'name@example.com'}
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label">{linked?.email ? 'รหัสผ่านใหม่' : 'รหัสผ่าน'}</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="อย่างน้อย 8 ตัวอักษร"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div>
              <label className="label">ยืนยันรหัสผ่าน</label>
              <input
                type="password"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !email.trim() || password.length < 8 || password !== confirm}
              className="btn-primary w-full text-sm"
            >
              {busy ? 'กำลังบันทึก…' : linked?.email ? 'อัปเดตรหัสผ่าน' : 'เปิดใช้งานเข้าระบบผ่านเว็บ'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function EmployeePortableZone() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ inserted: number; updated: number; total: number; errors: Array<{ row: number; emp_code: string; msg: string }> } | null>(null);

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const jwt = useAuthStore.getState().jwt;
      const res = await fetch(`${API_BASE}/owner/employees/import`, {
        method: 'POST',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
        body: form,
      });
      const json = (await res.json()) as { data?: typeof result; error?: string; message?: string };
      if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
      setResult(json.data ?? null);
      if (json.data) {
        toast.success(`นำเข้าแล้ว — เพิ่ม ${json.data.inserted} · อัปเดต ${json.data.updated} · ผิดพลาด ${json.data.errors.length}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'นำเข้าไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold mb-1">ย้ายข้อมูลพนักงาน (Owner เท่านั้น)</div>
      <p className="text-xs text-gray-500 mb-3">
        สำหรับย้ายรายชื่อพนักงานไป product อื่นของ HumanAI — ไม่มีเงินเดือน/เลขบัญชี.
        แก้ใน Excel แล้ว upload กลับมาก็ได้ (emp_code ซ้ำ = อัปเดต)
      </p>
      <div className="grid grid-cols-1 gap-2">
        <button
          onClick={() => downloadCsv('/owner/employees/portable-export', 'employees-portable.csv', toast.error)}
          className="btn-secondary text-sm"
        >
          <Download className="w-4 h-4" /> ดาวน์โหลด CSV
        </button>
        <label className={`btn-secondary text-sm cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-4 h-4" /> {busy ? 'กำลังนำเข้า…' : 'อัปโหลด CSV เพื่อนำเข้า'}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImport} disabled={busy} />
        </label>
      </div>
      {result && (
        <div className="mt-3 text-xs">
          <div className="text-gray-700">
            ทั้งหมด {result.total} · เพิ่ม <span className="font-semibold text-green-700">{result.inserted}</span> ·
            อัปเดต <span className="font-semibold text-blue-700">{result.updated}</span> ·
            ผิดพลาด <span className="font-semibold text-rose-700">{result.errors.length}</span>
          </div>
          {result.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-rose-700">ดูแถวที่ผิดพลาด ({result.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto pl-4 list-disc text-gray-600">
                {result.errors.map((er, i) => (
                  <li key={i}>แถว {er.row} {er.emp_code ? `(${er.emp_code})` : ''}: {er.msg}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteTenantZone() {
  const profile = useAuthStore((s) => s.profile);
  const clear = useAuthStore((s) => s.clear);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    setBusy(true);
    try {
      await apiFetch('/owner/delete-tenant', {
        method: 'POST',
        body: { confirmation_text: 'DELETE', tenant_code: code },
      });
      toast.success('ลบบริษัทเรียบร้อย — ออกจากระบบ');
      clear();
      window.location.href = '/';
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 border-red-200">
      <div className="font-semibold text-red-700 mb-2">🗑️ ลบบริษัท (Danger Zone)</div>
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-secondary text-sm text-red-700 border-red-200">
          <Trash2 className="w-4 h-4" /> ลบบริษัทและข้อมูลทั้งหมด
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-red-700">
            ⚠️ การลบจะทำลายกุญแจเข้ารหัสทันที — ข้อมูลเงินเดือนกู้คืนไม่ได้ และบริษัทจะถูก archive
          </p>
          <p className="text-xs text-gray-600">
            พิมพ์รหัสบริษัท <strong className="font-mono">{profile?.tenant_code}</strong> เพื่อยืนยัน
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="tenant_code"
            className="input font-mono"
          />
          <div className="flex gap-2">
            <button onClick={() => { setOpen(false); setCode(''); }} className="btn-secondary text-sm flex-1">ยกเลิก</button>
            <button
              onClick={handleDelete}
              disabled={busy || code.toUpperCase() !== (profile?.tenant_code ?? '').toUpperCase()}
              className="btn-danger text-sm flex-1"
            >
              {busy ? 'กำลังลบ…' : 'ยืนยันลบ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
